import { useCallback, useState, type FormEvent } from "react";
import { api } from "../lib/api";
import { Icon } from "./Icon";
import { TurnstileBox } from "./TurnstileBox";

type AuthMode = "login" | "register" | "reset";

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  onAuthenticated: () => Promise<void> | void;
}

export function AuthModal({ open, onClose, onAuthenticated }: AuthModalProps) {
  const [mode, setMode] = useState<AuthMode>("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const handleToken = useCallback((token: string) => setTurnstileToken(token), []);

  if (!open) return null;

  function changeMode(nextMode: AuthMode) {
    setMode(nextMode);
    setError("");
    setMessage("");
    setTurnstileToken("");
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    setMessage("");

    try {
      if (mode === "register") {
        await api.signUp({ name, email, password, turnstileToken });
        setMessage("注册成功，现在可以登录；邮箱验证将在邮件服务启用后开放。");
        setMode("login");
      } else if (mode === "reset") {
        await api.requestPasswordReset(email, turnstileToken);
        setMessage("邮件找回服务尚未启用，请联系站长处理密码重置。");
      } else {
        await api.signIn({ email, password, turnstileToken });
        await onAuthenticated();
        onClose();
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "操作失败，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-layer" role="presentation">
      <button className="modal-scrim" aria-label="关闭登录窗口" onClick={onClose} type="button" />
      <section aria-labelledby="auth-title" aria-modal="true" className="auth-modal" role="dialog">
        <header className="modal-heading">
          <div>
            <span className="section-index">账户</span>
            <h2 id="auth-title">{mode === "login" ? "登录今日聚合" : mode === "register" ? "创建账户" : "找回密码"}</h2>
          </div>
          <button className="icon-button" aria-label="关闭" onClick={onClose} type="button"><Icon name="close" /></button>
        </header>

        <form className="auth-form" onSubmit={handleSubmit}>
          {mode === "register" ? (
            <label>
              <span>昵称</span>
              <input autoComplete="name" maxLength={40} onChange={(event) => setName(event.target.value)} required value={name} />
            </label>
          ) : null}
          <label>
            <span>邮箱</span>
            <input autoComplete="email" onChange={(event) => setEmail(event.target.value)} required type="email" value={email} />
          </label>
          {mode !== "reset" ? (
            <label>
              <span>密码</span>
              <input
                autoComplete={mode === "register" ? "new-password" : "current-password"}
                minLength={8}
                onChange={(event) => setPassword(event.target.value)}
                required
                type="password"
                value={password}
              />
              {mode === "register" ? <small>至少 8 个字符</small> : null}
            </label>
          ) : null}

          <TurnstileBox onToken={handleToken} />
          {error ? <p className="form-message is-error">{error}</p> : null}
          {message ? <p className="form-message is-success">{message}</p> : null}
          <button className="primary-button" disabled={submitting} type="submit">
            {submitting ? "正在提交…" : mode === "login" ? "登录" : mode === "register" ? "注册" : "检查找回方式"}
          </button>
        </form>

        <footer className="auth-footer">
          {mode === "login" ? (
            <>
              <button onClick={() => changeMode("register")} type="button">没有账户？注册</button>
              <button onClick={() => changeMode("reset")} type="button">忘记密码</button>
            </>
          ) : (
            <button onClick={() => changeMode("login")} type="button">返回登录</button>
          )}
        </footer>
      </section>
    </div>
  );
}
